import React from "react";
import kiraSuperior from "../../../assets/kira-superior_2.png";
import { useUiLang } from "../../../context/UiLangContext";
import type { AppMode, RecordingStatus } from "../hooks/useTranslator";
import { Play, Square, ClipboardList } from "lucide-react";
import { useRecordingTimer } from "../hooks/useRecordingTimer";
import { HeaderKebabMenu } from "./HeaderKebabMenu";
import { Tooltip } from "../../../components/Tooltip";
import { UI_LABELS } from "./headerLabels";
import { HeaderModeToggle } from "./HeaderModeToggle";
import { HeaderLangSelectors } from "./HeaderLangSelectors";

interface AppHeaderProps {
  appMode: AppMode;
  recording: boolean;
  voiceEnabled: boolean;
  pipActive: boolean;
  status: RecordingStatus;
  statusText: string;
  srcLang: string;
  tgtLang: string;
  theme: "light" | "dark";
  meetingTranscriptLength: number;
  getStatusColorClass: () => string;
  setAppMode: (mode: AppMode) => void;
  setVoiceEnabled: (v: boolean) => void;
  setSrcLang: (lang: string) => void;
  setTgtLang: (lang: string) => void;
  onTogglePip: () => void;
  onToggleTheme: () => void;
  onOpenDocs: () => void;
  onOpenSummary: () => void;
  onToggleRecording: () => void;
  onLogout: () => void;
  onOpenProfile: () => void;
  onOpenHistory: () => void;
  onOpenSettings: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  appMode,
  recording,
  voiceEnabled,
  pipActive,
  status,
  statusText,
  srcLang,
  tgtLang,
  theme,
  meetingTranscriptLength,
  setAppMode,
  setVoiceEnabled,
  setSrcLang,
  setTgtLang,
  onTogglePip,
  onToggleTheme,
  onOpenDocs,
  onOpenSummary,
  onToggleRecording,
  onLogout,
  onOpenProfile,
  onOpenHistory,
  onOpenSettings,
}) => {
  const { uiLang, toggleUiLang } = useUiLang();
  const t = UI_LABELS[uiLang];
  const { formattedTime } = useRecordingTimer(recording);

  const statusBadgeClass =
    status === "connected"
      ? "text-[#22c55e] bg-[rgba(22,163,74,0.10)] border-[rgba(22,163,74,0.25)]"
      : status === "connecting"
      ? "text-yellow-400 bg-yellow-500/5 border-yellow-500/25"
      : status === "error"
      ? "text-rose-400 bg-rose-500/5 border-rose-500/25"
      : "text-[var(--text-dim)] bg-[var(--pill)] border-[var(--border)]";

  const statusDotClass =
    status === "connected"
      ? "bg-[#22c55e] animate-pulse shadow-[0_0_4px_#22c55e]"
      : status === "connecting"
      ? "bg-yellow-400"
      : status === "error"
      ? "bg-rose-400"
      : "bg-[var(--text-dim)]";

  const actBtn =
    "h-[27px] rounded-[8px] bg-[var(--pill)] border border-[var(--border)] text-[var(--text-muted)] cursor-pointer flex items-center gap-[4px] px-[8px] font-sans text-[11px] font-[500] transition-all duration-150 whitespace-nowrap flex-shrink-0 hover:bg-[var(--surface-hover)] hover:text-[var(--text)] hover:border-[var(--border-hover)] active:scale-95";

  return (
    <header
      id="app-header"
      className="flex items-center bg-[var(--sidebar-bg)] border-b border-[var(--border)] px-[12px] h-[48px] flex-shrink-0 select-none overflow-x-auto overflow-y-hidden gap-0 transition-colors duration-300 z-10"
      style={{ scrollbarWidth: "none" }}
    >
      {/* ── LEFT ── */}
      <div className="flex items-center gap-[8px] flex-[0_0_auto]">
        <div className="flex gap-[6px] items-center mr-[4px]">
          <div className="w-[12px] h-[12px] rounded-full bg-[var(--mac-close)] hover:brightness-110 cursor-pointer transition-filter duration-100" title="Cerrar" />
          <div className="w-[12px] h-[12px] rounded-full bg-[var(--mac-minimize)] hover:brightness-110 cursor-pointer transition-filter duration-100" title="Minimizar" />
          <div className="w-[12px] h-[12px] rounded-full bg-[var(--mac-maximize)] hover:brightness-110 cursor-pointer transition-filter duration-100" title="Maximizar" />
        </div>

        <div className="flex items-center cursor-default">
          <img src={kiraSuperior} className="h-[36px] w-auto object-contain flex-shrink-0" alt="Kira" />
        </div>

        <Tooltip text="Estado de la sesión actual">
          <div
            className={`flex items-center gap-[4px] text-[10px] font-[700] tracking-[0.05em] px-[8px] py-[2px] rounded-full border whitespace-nowrap transition-all duration-150 ${statusBadgeClass}`}
            aria-label="Estado de conexión"
          >
            <div className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${statusDotClass}`} />
            <span>
              {statusText}
              {recording && (
                <span className="ml-1 font-mono">{formattedTime}</span>
              )}
            </span>
          </div>
        </Tooltip>
      </div>

      {/* ── CENTER ── */}
      <div className="flex items-center gap-[5px] flex-1 justify-center min-w-0 px-[12px]">
        <HeaderModeToggle
          appMode={appMode}
          recording={recording}
          modeTraductorLabel={t.modeTraductor}
          modeMinutaLabel={t.modeMinuta}
          modeAlert={t.modeAlert}
          setAppMode={setAppMode}
        />

        <div className="w-[1px] h-[16px] bg-[var(--border)] flex-shrink-0 mx-[3px]" />

        <HeaderLangSelectors
          appMode={appMode}
          recording={recording}
          srcLang={srcLang}
          tgtLang={tgtLang}
          fromLabel={t.from}
          toLabel={t.to}
          minutaInLabel={t.minutaIn}
          setSrcLang={setSrcLang}
          setTgtLang={setTgtLang}
        />

        <div className="w-[1px] h-[16px] bg-[var(--border)] flex-shrink-0 mx-[3px]" />

        <Tooltip text={recording ? "Detener sesión" : "Iniciar sesión (Espacio)"}>
          <button
            onClick={onToggleRecording}
            aria-label={recording ? t.stop : t.start}
            className={`font-sans text-[12.5px] font-[700] border-none cursor-pointer px-[16px] py-[5px] rounded-full flex items-center gap-[5px] whitespace-nowrap flex-shrink-0 transition-all duration-150 active:scale-95 ${
              recording
                ? "bg-[#ef4444] text-white shadow-[0_2px_10px_rgba(239,68,68,0.3)] hover:brightness-110 hover:shadow-[0_4px_16px_rgba(239,68,68,0.4)] hover:-translate-y-px"
                : "bg-[#16a34a] text-white shadow-[0_2px_10px_rgba(22,163,74,0.35)] hover:brightness-110 hover:shadow-[0_4px_16px_rgba(22,163,74,0.45)] hover:-translate-y-px"
            }`}
          >
            {recording ? <Square size={10} fill="currentColor" /> : <Play size={10} fill="currentColor" />}
            <span>{recording ? t.stop : t.start}</span>
          </button>
        </Tooltip>

        {recording && (
          <Tooltip text="Duración de la sesión en curso">
            <div
              className="flex items-center gap-[4px] text-[11px] font-[700] font-mono text-[#f87171] bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.18)] px-[9px] py-[2px] rounded-full whitespace-nowrap flex-shrink-0"
              aria-live="polite"
            >
              <div className="w-[5px] h-[5px] rounded-full bg-[#f87171] animate-pulse" />
              <span>{formattedTime}</span>
            </div>
          </Tooltip>
        )}
      </div>

      {/* ── RIGHT ── */}
      <div className="flex items-center gap-[3px] flex-[0_0_auto]">
        <Tooltip text={meetingTranscriptLength === 0 ? "Grabá una sesión primero" : "Generar minuta de reunión"}>
          <button
            onClick={onOpenSummary}
            disabled={meetingTranscriptLength === 0}
            aria-label="Generar minuta"
            className={`${actBtn} ${meetingTranscriptLength === 0 ? "opacity-40 pointer-events-none" : ""}`}
          >
            <ClipboardList size={12} />
            {t.minuta}
          </button>
        </Tooltip>

        <HeaderKebabMenu
          appMode={appMode}
          voiceEnabled={voiceEnabled}
          pipActive={pipActive}
          theme={theme}
          uiLang={uiLang}
          t={t}
          onToggleVoice={() => setVoiceEnabled(!voiceEnabled)}
          onTogglePip={onTogglePip}
          onOpenDocs={onOpenDocs}
          onOpenProfile={onOpenProfile}
          onOpenHistory={onOpenHistory}
          onOpenSettings={onOpenSettings}
          onToggleTheme={onToggleTheme}
          onToggleUiLang={toggleUiLang}
          onLogout={onLogout}
        />
      </div>
    </header>
  );
};
